import React, { useState, useEffect } from "react";
import {
  UserPlus,
  Users,
  Search,
  Phone,
  MapPin,
  Banknote,
  Trash2,
  Edit3,
  X,
  Camera,
  FileText,
  LayoutGrid,
  List,
  Upload,
  Paperclip,
  MinusCircle,
  PlusCircle,
  Eye,
  Download,
} from "lucide-react";
import api from "../api/axiosInstance";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const NewEmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingEmp, setViewingEmp] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    mobile_no: "",
    email: "",
    dob: "",
    joining_date: "",
    address: "",
    assigned_site_id: "",
    pay_type: "Daily",
    rate: "",
    bank_name: "",
    account_no: "",
    ifsc_code: "",
    status: "Active",
  });

  // NEW STATE: Salary Structure Components
  const [salaryStructure, setSalaryStructure] = useState([]);

  const [profilePic, setProfilePic] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [docFiles, setDocFiles] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [empRes, projRes] = await Promise.all([
        api.get("get_employees.php"),
        api.get("get_projects.php"),
      ]);
      setEmployees(empRes.data.employees || []);
      setProjects(projRes.data.projects || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addComponent = (type) => {
    setSalaryStructure([
      ...salaryStructure,
      {
        id: Date.now(),
        name: "",
        amount: "",
        type: type, // 'addition' or 'deduction'
        calculation_type: "fixed", // 'fixed' or 'percentage'
        frequency: "monthly", // 'monthly' or 'per_day'
      },
    ]);
  };

  const updateComponent = (id, field, value) => {
    setSalaryStructure(
      salaryStructure.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  const removeComponent = (id) => {
    setSalaryStructure(salaryStructure.filter((c) => c.id !== id));
  };

  const handleView = (emp) => {
    setViewingEmp(emp);
    setIsViewModalOpen(true);
  };

  const handleEdit = (emp) => {
    setEditingId(emp.id);
    setFormData({ ...emp });

    // CHANGE THIS LINE:
    // If it's already an object (from my new PHP), don't parse it.
    // If it's a string (old data), parse it.
    const structure =
      typeof emp.salary_structure === "string"
        ? JSON.parse(emp.salary_structure)
        : emp.salary_structure || [];

    setSalaryStructure(structure);

    setProfilePreview(
      emp.profile_pic
        ? `https://backendibos.laxmaninterior.in/uploads/profiles/${emp.profile_pic}`
        : null,
    );
    setIsModalOpen(true);
  };
  const handleDocChange = (e) => {
    setDocFiles(Array.from(e.target.files));
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePic(file);
      setProfilePreview(URL.createObjectURL(file));
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This action is permanent.")) return;
    try {
      await api.post("delete_employee.php", { id });
      loadData();
    } catch (err) {
      alert("Delete failed");
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = editingId ? "update_employee.php" : "save_employee.php";

    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));

    // Append the salary structure as a JSON string
    data.append("salary_structure", JSON.stringify(salaryStructure));

    if (editingId) data.append("id", editingId);
    if (profilePic) data.append("profile_pic", profilePic);
    docFiles.forEach((file, i) => data.append(`doc_${i}`, file));

    try {
      await api.post(endpoint, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIsModalOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      alert("Save failed");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      pay_type: "Daily",
      status: "Active",
      name: "",
      mobile_no: "",
      email: "",
      dob: "",
      joining_date: "",
      address: "",
      assigned_site_id: "",
      rate: "",
      bank_name: "",
      account_no: "",
      ifsc_code: "",
    });
    setSalaryStructure([]);
    setProfilePic(null);
    setProfilePreview(null);
    setDocFiles([]);
  };

  const downloadIDCard = async (empId, empName) => {
    try {
      const response = await api.get(`generate_id_card.php?id=${empId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `ID_Card_${empName.replace(/\s+/g, "_")}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("ID Card Generation Failed");
    }
  };

  const filteredEmployees = employees.filter((e) =>
    e.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const exportCSV = () => {
    const headers = ['ID', 'Name', 'Mobile', 'Pay Type', 'Rate', 'Assigned Site', 'Status'];
    const csvContent = [
      headers.join(','),
      ...filteredEmployees.map(e => [
        `EMP-${e.id.toString().padStart(4, '0')}`,
        `"${e.name}"`,
        `"${e.mobile_no}"`,
        `"${e.pay_type}"`,
        e.rate,
        `"${e.assigned_site_name || 'Unassigned'}"`,
        `"${e.status || 'Active'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'employees_list.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportExcel = () => {
    const data = filteredEmployees.map(e => ({
      'ID': `EMP-${e.id.toString().padStart(4, '0')}`,
      'Name': e.name,
      'Mobile': e.mobile_no,
      'Pay Type': e.pay_type,
      'Rate': parseFloat(e.rate),
      'Assigned Site': e.assigned_site_name || 'Unassigned',
      'Status': e.status || 'Active'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    XLSX.writeFile(wb, "employees_list.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Employees List", 14, 15);
    const tableColumn = ["ID", "Name", "Mobile", "Pay Type", "Rate", "Assigned Site", "Status"];
    const tableRows = [];

    filteredEmployees.forEach(e => {
      const rowData = [
        `EMP-${e.id.toString().padStart(4, '0')}`,
        e.name,
        e.mobile_no,
        e.pay_type,
        e.rate,
        e.assigned_site_name || 'Unassigned',
        e.status || 'Active'
      ];
      tableRows.push(rowData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });
    doc.save("employees_list.pdf");
  };

  if (loading)
    return (
      <div className="p-20 text-center font-black animate-pulse text-slate-300">
        Syncing Workforce...
      </div>
    );

  return (
    <div className="space-y-8 p-4 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase leading-none">
            Workforce Master
          </h1>
          <p className="text-slate-500 font-medium italic mt-2">
            Employee & Labour Management Console
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* View Switcher */}
          <div className="bg-slate-100 p-1 rounded-xl flex gap-1 shadow-inner">
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded-lg transition-all ${viewMode === "table" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400"}`}
            >
              <List size={20} />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg transition-all ${viewMode === "grid" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400"}`}
            >
              <LayoutGrid size={20} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="bg-green-50 text-green-600 px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-green-600 hover:text-white transition-all shadow-sm flex items-center gap-1">
              CSV
            </button>
            <button onClick={exportExcel} className="bg-emerald-50 text-emerald-600 px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex items-center gap-1">
              Excel
            </button>
            <button onClick={exportPDF} className="bg-rose-50 text-rose-600 px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-rose-600 hover:text-white transition-all shadow-sm flex items-center gap-1">
              PDF
            </button>
          </div>

          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2 hover:bg-indigo-600 transition-all"
          >
            <UserPlus size={18} /> Register Staff
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
        <Search className="text-slate-300" size={20} />
        <input
          className="w-full bg-transparent outline-none font-bold text-sm uppercase"
          placeholder="Search by name, mobile, or site..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Content View */}
      {viewMode === "table" ? (
        <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                  Employee
                </th>
                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                  Mobile
                </th>
                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                  Assigned Site
                </th>
                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                  Rate
                </th>
                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                  KYC
                </th>
                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredEmployees.map((emp) => (
                <tr
                  key={emp.id}
                  className="group hover:bg-slate-50/50 transition-all"
                >
                  <td className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 overflow-hidden border border-slate-200">
                        {emp.profile_pic ? (
                          <img
                            src={`https://backendibos.laxmaninterior.in/uploads/profiles/${emp.profile_pic}`}
                            className="w-full h-full object-cover"
                            alt=""
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <Users size={16} />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-black text-slate-900 text-sm uppercase italic leading-none">
                          {emp.name}
                        </p>
                        <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">
                          ID: EMP-{emp.id.toString().padStart(4, "0")}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-6 text-xs font-bold text-slate-600">
                    {emp.mobile_no}
                  </td>
                  <td className="p-6">
                    <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[9px] font-black uppercase">
                      {emp.assigned_site_name || "Unassigned"}
                    </span>
                  </td>
                  <td className="p-6">
                    <p className="font-black text-slate-900 text-xs italic">
                      ₹{parseFloat(emp.rate).toLocaleString()}
                    </p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                      Per{" "}
                      {emp.pay_type === "Monthly"
                        ? "Month"
                        : emp.pay_type === "Hourly"
                          ? "Hour"
                          : "Day"}
                    </p>
                  </td>
                  <td className="p-6">
                    <div className="flex items-center gap-1 text-slate-400">
                      <FileText
                        size={14}
                        className={emp.documents ? "text-indigo-500" : ""}
                      />
                      <span className="text-[10px] font-bold">
                        {emp.documents ? JSON.parse(emp.documents).length : 0}{" "}
                        Docs
                      </span>
                    </div>
                  </td>
                  <td className="p-6 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleView(emp)}
                        className="p-2 text-slate-400 hover:text-sky-500"
                        title="View"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => downloadIDCard(emp.id, emp.name)}
                        className="p-2 text-slate-400 hover:text-indigo-600"
                        title="ID Card"
                      >
                        <FileText size={18} />
                      </button>
                      <button
                        onClick={() => handleEdit(emp)}
                        className="p-2 text-slate-400 hover:text-slate-900"
                        title="Edit"
                      >
                        <Edit3 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(emp.id)}
                        className="p-2 text-slate-400 hover:text-rose-500"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEmployees.map((emp) => (
            <div
              key={emp.id}
              className="group bg-white p-8 rounded-[48px] border border-slate-100 shadow-sm hover:shadow-2xl transition-all relative overflow-hidden"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-3xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 border-2 border-slate-50">
                  {emp.profile_pic ? (
                    <img
                      src={`https://backendibos.laxmaninterior.in/uploads/profiles/${emp.profile_pic}`}
                      className="w-full h-full object-cover"
                      alt=""
                    />
                  ) : (
                    <Users size={32} />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 uppercase italic leading-none">
                    {emp.name}
                  </h3>
                  <span className="text-[9px] font-black uppercase text-indigo-500 tracking-widest">
                    {emp.pay_type} Basis
                  </span>
                </div>
              </div>
              {/* ... (Existing Card details for site, mobile, etc.) ... */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleView(emp)}
                  className="flex-1 py-3 bg-sky-50 text-sky-600 rounded-2xl font-black text-[10px] uppercase hover:bg-sky-500 hover:text-white transition-all shadow-sm"
                >
                  View
                </button>
                <button
                  onClick={() => handleEdit(emp)}
                  className="flex-1 py-3 bg-slate-50 text-slate-900 rounded-2xl font-black text-[10px] uppercase hover:bg-indigo-600 hover:text-white transition-all"
                >
                  Edit
                </button>
                <button
                  onClick={() => downloadIDCard(emp.id, emp.name)}
                  className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                >
                  <FileText size={18} />
                </button>
                <button
                  onClick={() => handleDelete(emp.id)}
                  className="p-3 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-500 hover:text-white transition-all"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isViewModalOpen && viewingEmp && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white w-full max-w-2xl rounded-[48px] shadow-2xl p-12 space-y-8 overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-black italic tracking-tighter uppercase">
                Employee Details
              </h2>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-all"
              >
                <X />
              </button>
            </div>

            <div className="flex items-center gap-6 p-6 bg-slate-50 rounded-[32px]">
              <div className="w-24 h-24 bg-white rounded-full border-4 border-white shadow-md overflow-hidden flex items-center justify-center">
                {viewingEmp.profile_pic ? (
                  <img
                    src={`https://backendibos.laxmaninterior.in/uploads/profiles/${viewingEmp.profile_pic}`}
                    className="w-full h-full object-cover"
                    alt=""
                  />
                ) : (
                  <Users className="text-slate-300" size={32} />
                )}
              </div>
              <div>
                <h3 className="text-2xl font-black text-slate-900 uppercase italic leading-none">
                  {viewingEmp.name}
                </h3>
                <p className="text-xs font-bold text-slate-400 uppercase mt-1">
                  ID: EMP-{viewingEmp.id.toString().padStart(4, "0")}
                </p>
                <span className="inline-block mt-2 bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[9px] font-black uppercase">
                  {viewingEmp.status || "Active"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">
                  Personal Information
                </h4>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Mobile
                  </p>
                  <p className="font-black text-slate-900">
                    {viewingEmp.mobile_no || "N/A"}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Email
                  </p>
                  <p className="font-black text-slate-900">
                    {viewingEmp.email || "N/A"}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    DOB
                  </p>
                  <p className="font-black text-slate-900">
                    {viewingEmp.dob || "N/A"}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Address
                  </p>
                  <p className="font-black text-slate-900">
                    {viewingEmp.address || "N/A"}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">
                  Work & Pay Details
                </h4>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Assigned Site
                  </p>
                  <p className="font-black text-slate-900">
                    {viewingEmp.assigned_site_name || "Unassigned"}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Pay Type
                  </p>
                  <p className="font-black text-slate-900">
                    {viewingEmp.pay_type}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Rate
                  </p>
                  <p className="font-black text-slate-900">
                    ₹{parseFloat(viewingEmp.rate || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    Bank Details
                  </p>
                  <p className="font-black text-slate-900">
                    {viewingEmp.bank_name || "N/A"}
                  </p>
                  {viewingEmp.account_no && (
                    <p className="text-xs font-bold text-slate-500">
                      A/c: {viewingEmp.account_no}
                    </p>
                  )}
                  {viewingEmp.ifsc_code && (
                    <p className="text-xs font-bold text-slate-500">
                      IFSC: {viewingEmp.ifsc_code}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Display Automated Salary Structure in read-only format if present */}
            {viewingEmp.salary_structure && (() => {
              try {
                const struct = typeof viewingEmp.salary_structure === "string"
                  ? JSON.parse(viewingEmp.salary_structure)
                  : viewingEmp.salary_structure;

                if (struct && Array.isArray(struct) && struct.length > 0) {
                  return (
                    <div className="space-y-4 pt-4 border-t border-slate-100">
                      <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">
                        Salary Structure
                      </h4>
                      <div className="space-y-2">
                        {struct.map((comp, idx) => (
                          <div key={idx} className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-lg ${comp.type === "addition" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
                                {comp.type === "addition" ? <PlusCircle size={14} /> : <MinusCircle size={14} />}
                              </div>
                              <div>
                                <p className="font-bold text-slate-900 text-xs uppercase">{comp.name || 'Unnamed'}</p>
                                <p className="text-[9px] font-black uppercase text-slate-400">
                                  {comp.calculation_type === 'fixed'
                                    ? (comp.frequency === 'per_day' ? 'Per Day Worked' : 'Fixed Monthly')
                                    : 'Percentage Based'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-black ${comp.type === 'addition' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {comp.calculation_type === 'fixed' ? '₹' : ''}
                                {comp.amount}
                                {comp.calculation_type === 'percentage' ? '%' : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              } catch (e) {
                return null;
              }
              return null;
            })()}

          </div>
        </div>
      )}

      {/* Modal Logic Remains the Same */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
          <form
            onSubmit={handleSubmit}
            className="bg-white w-full max-w-4xl rounded-[48px] shadow-2xl p-12 space-y-8 overflow-y-auto max-h-[90vh]"
          >
            {/* (Modal Form Content) */}
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-black italic tracking-tighter uppercase">
                {editingId ? "Edit Profile" : "New Registration"}
              </h2>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-2 bg-slate-100 rounded-full"
              >
                <X />
              </button>
            </div>

            {/* Profile Pic Upload */}
            <div className="flex items-center gap-6 p-6 bg-slate-50 rounded-[32px]">
              <div className="relative group">
                <div className="w-24 h-24 bg-white rounded-full border-4 border-white shadow-md overflow-hidden flex items-center justify-center">
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      className="w-full h-full object-cover"
                      alt=""
                    />
                  ) : (
                    <Camera className="text-slate-300" size={32} />
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-slate-900 text-white p-2 rounded-full cursor-pointer hover:bg-indigo-600 transition-all">
                  <Upload size={14} />
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </label>
              </div>
              <div>
                <h4 className="font-black text-xs uppercase text-slate-900">
                  Worker Profile Photo
                </h4>
                <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">
                  Required for ID generation & site attendance
                </p>
              </div>
            </div>

            {/* Input Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">
                  Personal Information
                </h4>
                <input
                  required
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                    placeholder="Mobile"
                    value={formData.mobile_no}
                    onChange={(e) =>
                      setFormData({ ...formData, mobile_no: e.target.value })
                    }
                  />
                  <input
                    type="date"
                    className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                    value={formData.dob}
                    onChange={(e) =>
                      setFormData({ ...formData, dob: e.target.value })
                    }
                  />
                </div>
                <textarea
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm h-24"
                  placeholder="Full Address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
              </div>

              <div className="space-y-4">
                <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">
                  Work & Pay Details
                </h4>
                <select
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                  value={formData.assigned_site_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      assigned_site_id: e.target.value,
                    })
                  }
                >
                  <option value="">Assign to Project...</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_name}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-4">
                  <select
                    className="p-4 bg-slate-50 rounded-2xl font-bold"
                    value={formData.pay_type}
                    onChange={(e) =>
                      setFormData({ ...formData, pay_type: e.target.value })
                    }
                  >
                    <option value="Daily">Daily</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Hourly">Hourly</option>
                  </select>
                  <input
                    required

                    className="p-4 bg-slate-50 rounded-2xl font-black"
                    placeholder="Rate (₹)"
                    value={formData.rate}
                    onChange={(e) =>
                      setFormData({ ...formData, rate: e.target.value })
                    }
                  />
                </div>
                <div className="mt-4">
                  <h4 className="text-[10px] font-black uppercase text-slate-400 mb-2">
                    KYC Documents
                  </h4>
                  <label className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:border-indigo-400 transition-all">
                    <Paperclip size={18} className="text-slate-400" />
                    <span className="text-xs font-bold text-slate-500 uppercase">
                      {docFiles.length > 0
                        ? `${docFiles.length} Files`
                        : "Attach Docs"}
                    </span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleDocChange}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">
                Automated Salary Structure
              </h4>
              <div className="space-y-3 bg-slate-50 p-6 rounded-[32px] border border-slate-100">
                {salaryStructure.map((comp) => (
                  <div
                    key={comp.id}
                    className="grid grid-cols-12 gap-2 items-center bg-white p-3 rounded-2xl shadow-sm border border-slate-50"
                  >
                    {/* Icon & Name */}
                    <div className="col-span-4 flex items-center gap-2">
                      <div
                        className={`p-2 rounded-lg ${comp.type === "addition" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}
                      >
                        {comp.type === "addition" ? (
                          <PlusCircle size={14} />
                        ) : (
                          <MinusCircle size={14} />
                        )}
                      </div>
                      <input
                        className="w-full bg-transparent font-bold text-[10px] outline-none"
                        placeholder="e.g. PF or Tea"
                        value={comp.name}
                        onChange={(e) =>
                          updateComponent(comp.id, "name", e.target.value)
                        }
                      />
                    </div>

                    {/* Amount & Type Toggle */}
                    <div className="col-span-3 flex items-center bg-slate-50 rounded-lg px-2">
                      <input
                        className="w-full bg-transparent p-2 font-black text-[10px] text-right outline-none"
                        placeholder="0"
                        value={comp.amount}
                        onChange={(e) =>
                          updateComponent(comp.id, "amount", e.target.value)
                        }
                      />
                      <select
                        className="bg-transparent font-black text-[9px] uppercase cursor-pointer"
                        value={comp.calculation_type}
                        onChange={(e) =>
                          updateComponent(
                            comp.id,
                            "calculation_type",
                            e.target.value,
                          )
                        }
                      >
                        <option value="fixed">₹</option>
                        <option value="percentage">%</option>
                      </select>
                    </div>

                    {/* Frequency (Only relevant for Fixed amounts) */}
                    <div className="col-span-4">
                      <select
                        disabled={comp.calculation_type === "percentage"}
                        className={`w-full p-2 rounded-lg font-black text-[9px] uppercase outline-none ${comp.calculation_type === "percentage" ? "bg-slate-100 text-slate-300" : "bg-slate-50"}`}
                        value={comp.frequency}
                        onChange={(e) =>
                          updateComponent(comp.id, "frequency", e.target.value)
                        }
                      >
                        <option value="monthly">Fixed Monthly</option>
                        <option value="per_day">Per Day Worked</option>
                      </select>
                    </div>

                    <div className="col-span-1 text-right">
                      <button
                        type="button"
                        onClick={() => removeComponent(comp.id)}
                        className="text-slate-300 hover:text-rose-500"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => addComponent("addition")}
                    className="flex-1 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[9px] font-black uppercase flex items-center justify-center gap-1 hover:bg-emerald-100"
                  >
                    + Addition
                  </button>
                  <button
                    type="button"
                    onClick={() => addComponent("deduction")}
                    className="flex-1 py-2 bg-rose-50 text-rose-600 rounded-xl text-[9px] font-black uppercase flex items-center justify-center gap-1 hover:bg-rose-100"
                  >
                    + Deduction
                  </button>
                </div>
              </div>
            </div>
            <button
              type="submit"
              className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-indigo-600 transition-all"
            >
              {editingId ? "Update Record" : "Finalize Registration"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default NewEmployeeManagement;
